import AccessDatesPicker from '@components/provisioning/custom-provision/AccessDatesPicker';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Notifier from '@components/common/Notifier';
import PropTypes from 'prop-types';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {JIRA_KEY_RE, formatErrorMsg, getError} from '@account/utils/errors';
import {addProductAccessTerm} from '@account/api';
import {getPATDay, humanDateStamp} from '@account/utils/dates';
import {useMutation, useQueryClient} from '@tanstack/react-query';

const AddTermModal = ({
  open,
  onClose,
  productAccessId,
  productAccess,
  existingTerms,
  onHighlightTerm,
}) => {
  const queryClient = useQueryClient();
  const isUnlimited = productAccess.unlimited_quota ?? false;

  const [startDate, setStartDate] = useState(() => {
    const validTerms = (existingTerms ?? []).filter(t => t.end_date);
    if (!validTerms.length) return null;
    const latestEnd = validTerms.reduce((max, t) =>
      getPATDay(t.end_date) > getPATDay(max.end_date) ? t : max
    ).end_date;
    return getPATDay(latestEnd).plus({days: 1});
  });

  const [endDate, setEndDate] = useState(() => {
    const validTerms = (existingTerms ?? []).filter(t => t.end_date);
    if (!validTerms.length) return null;
    const latestEnd = validTerms.reduce((max, t) =>
      getPATDay(t.end_date) > getPATDay(max.end_date) ? t : max
    ).end_date;
    return getPATDay(latestEnd).plus({days: 1, years: 1}).minus({days: 1});
  });

  const [quotaLimitRaw, setQuotaLimitRaw] = useState('');
  const [supportTicketRef, setSupportTicketRef] = useState('');
  const [notes, setNotes] = useState('');
  const [serverError, setServerError] = useState(null);
  const [serverBlockingTerm, setServerBlockingTerm] = useState(null);

  const quotaLimit = quotaLimitRaw === '' ? null : Number(quotaLimitRaw);

  const userEditedRef = useRef(false);
  const hasInitializedRef = useRef(false);

  const handleResetDates = useCallback(() => {
    const validTerms = (existingTerms ?? []).filter(t => t.end_date);
    if (!validTerms.length) return;
    const latestEnd = validTerms.reduce((max, t) =>
      getPATDay(t.end_date) > getPATDay(max.end_date) ? t : max
    ).end_date;
    const start = getPATDay(latestEnd).plus({days: 1});
    setStartDate(start);
    setEndDate(start.plus({years: 1}).minus({days: 1}));
    userEditedRef.current = false;
  }, [existingTerms]);

  // Seed the default dates once, the first time we have terms to derive them
  // from. Re-running on every fresh `existingTerms` reference would clobber the
  // user's date selection.
  useEffect(() => {
    if (hasInitializedRef.current || userEditedRef.current) return;
    const hasValidTerms = (existingTerms ?? []).some(t => t.end_date);
    if (!hasValidTerms) return;
    hasInitializedRef.current = true;
    handleResetDates();
  }, [existingTerms, handleResetDates]);

  const overlappingTerm = useMemo(() => {
    if (!startDate || !endDate) return null;
    return (existingTerms ?? []).find(t => {
      if (!t.start_date || !t.end_date) return false;
      const tStart = getPATDay(t.start_date);
      const tEnd = getPATDay(t.end_date);
      return (
        startDate.startOf('day') <= tEnd && endDate.startOf('day') >= tStart
      );
    });
  }, [startDate, endDate, existingTerms]);

  const canSubmit =
    startDate !== null &&
    endDate !== null &&
    startDate < endDate &&
    !overlappingTerm &&
    (isUnlimited ||
      (quotaLimit !== null && !isNaN(quotaLimit) && quotaLimit > 0)) &&
    JIRA_KEY_RE.test(supportTicketRef.trim()) &&
    !!notes.trim();

  const {mutate, isLoading} = useMutation(
    () =>
      addProductAccessTerm(productAccessId, {
        start_date: startDate.toISODate(),
        end_date: endDate.toISODate(),
        has_unlimited_quota: isUnlimited,
        quota_limit: isUnlimited ? null : quotaLimit,
        quota_units: productAccess.quota_units,
        support_ticket_ref: supportTicketRef.trim(),
        notes: notes.trim(),
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['productAccessTerms', productAccessId]);
        queryClient.invalidateQueries(['productAccess', productAccessId]);
        onClose();
      },
      onError: err => {
        const error = getError(err);
        if (error.statusCode === 422 && error.conflicting_pat_id) {
          setServerBlockingTerm(error);
        } else {
          setServerError(formatErrorMsg(error));
        }
      },
    }
  );

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick') return;
          handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <span>Add access period</span>
            <Tooltip title="Creates a new time window during which this product is active, with its own quota allocation.">
              <InfoOutlinedIcon fontSize="small" color="disabled" />
            </Tooltip>
          </Stack>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{position: 'absolute', right: 8, top: 8}}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={3} sx={{mt: 1}}>
            <Alert severity="warning" variant="outlined">
              This bypasses the standard ordering process. Requires a JIRA
              ticket and notes for the audit trail.
            </Alert>

            {existingTerms?.length > 0 && (
              <Box
                sx={theme => ({
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.divider}`,
                  p: 2,
                  bgcolor: 'grey.50',
                })}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Existing access periods
                </Typography>
                <Stack spacing={0.5}>
                  {existingTerms.map(t => (
                    <Typography key={t.id} variant="body2">
                      {humanDateStamp(t.start_date)} &ndash;{' '}
                      {humanDateStamp(t.end_date)}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            <Stack spacing={0.5}>
              <AccessDatesPicker
                startDate={startDate}
                endDate={endDate}
                onChangeStartDate={date => {
                  userEditedRef.current = true;
                  setStartDate(date);
                  setServerBlockingTerm(null);
                  setServerError(null);
                }}
                onChangeEndDate={date => {
                  userEditedRef.current = true;
                  setEndDate(date);
                  setServerBlockingTerm(null);
                  setServerError(null);
                }}
                onBlurStartDate={() => {}}
                onBlurEndDate={() => {}}
              />
              {existingTerms?.length > 0 && (
                <Link
                  component="button"
                  variant="caption"
                  color="text.secondary"
                  onClick={handleResetDates}
                  sx={{alignSelf: 'flex-start'}}
                >
                  Restore suggested dates
                </Link>
              )}
            </Stack>

            {overlappingTerm && (
              <Alert severity="error" variant="outlined">
                These dates overlap with an existing access period (
                {humanDateStamp(overlappingTerm.start_date)} &ndash;{' '}
                {humanDateStamp(overlappingTerm.end_date)}).{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    onHighlightTerm(overlappingTerm.id);
                    handleClose();
                  }}
                >
                  Show conflicting period
                </Link>
              </Alert>
            )}

            {serverBlockingTerm && (
              <Alert severity="error" variant="outlined">
                These dates overlap with an existing access period (
                {humanDateStamp(serverBlockingTerm.conflicting_pat_start_date)}{' '}
                &ndash;{' '}
                {humanDateStamp(serverBlockingTerm.conflicting_pat_end_date)}).
                Adjust your dates or delete the conflicting period first.{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={() => {
                    onHighlightTerm(serverBlockingTerm.conflicting_pat_id);
                    handleClose();
                  }}
                >
                  Show conflicting period
                </Link>
              </Alert>
            )}

            {!isUnlimited && (
              <TextField
                label="Quota limit"
                type="number"
                value={quotaLimitRaw}
                onChange={e => setQuotaLimitRaw(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {productAccess.quota_units}
                    </InputAdornment>
                  ),
                }}
                fullWidth
                required
              />
            )}

            <TextField
              label="Support ticket ref"
              value={supportTicketRef}
              onChange={e => setSupportTicketRef(e.target.value)}
              placeholder="CGO-1234 — RevOps approval ticket"
              error={
                !!supportTicketRef.trim() &&
                !JIRA_KEY_RE.test(supportTicketRef.trim())
              }
              helperText={
                supportTicketRef.trim() &&
                !JIRA_KEY_RE.test(supportTicketRef.trim())
                  ? 'Must match format PROJECT-1234 (e.g. CGO-1234)'
                  : 'Format: PROJECT-1234 (e.g. CGO-1234)'
              }
              fullWidth
              required
            />

            <TextField
              label="Notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              helperText="Explain why this access period is being added outside the standard provisioning flow."
              multiline
              minRows={3}
              fullWidth
              required
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined" disabled={isLoading}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={() => mutate()}
            variant="contained"
            disabled={!canSubmit || isLoading}
          >
            {isLoading ? 'Saving…' : 'Add access period'}
          </Button>
        </DialogActions>
      </Dialog>

      <Notifier open={isLoading} message="Adding access period…" progress />
      <Notifier
        open={!!serverError}
        message={serverError}
        severity="error"
        autoHideDuration={8000}
        onClose={() => setServerError(null)}
      />
    </>
  );
};

AddTermModal.propTypes = {
  existingTerms: PropTypes.arrayOf(
    PropTypes.shape({
      end_date: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      start_date: PropTypes.string,
    })
  ),
  onClose: PropTypes.func.isRequired,
  onHighlightTerm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  productAccess: PropTypes.shape({
    quota_units: PropTypes.string,
    unlimited_quota: PropTypes.bool,
  }).isRequired,
  productAccessId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
};

export default AddTermModal;
